import { zodResolver } from '@hookform/resolvers/zod';
import { Action, Modal, TextAreaInput, useToast } from 'corso-admin-ui';
import { crewMerchantShippingClaimApi } from 'corso-types';
import { useForm } from 'react-hook-form';
import { isErrorMessage } from '~/utils/errorResponse';
import {
  ShippingClaimRestore,
  useShippingClaimRestore,
} from '../hooks/useShippingClaimRestore';

const restoreSchema =
  crewMerchantShippingClaimApi[
    '/:storeId/shipping-protection/claim/:shippingClaimId/restore'
  ].post.request.body;

export function RestoreShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { mutate: restoreClaim, isPending } = useShippingClaimRestore();

  const formId = 'restore-shipping-claim-form';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(restoreSchema),
    defaultValues: { comment: '' } satisfies ShippingClaimRestore,
  });

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title="Restore Claim"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Restore
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit((data) =>
            restoreClaim(data, {
              onSuccess: closeAndReset,
              onError: (error) =>
                toast.show(
                  isErrorMessage(error)
                    ? error.message
                    : 'Could not restore this claim. Please try again.',
                ),
            }),
          )(e).catch((error: unknown) =>
            console.error('Failed to restore shipping claim', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <TextAreaInput
          id="restore-shipping-claim-comment"
          label="Comment"
          details="This comment will be added to the timeline."
          rows={3}
          {...register('comment')}
          error={errors.comment?.message}
        />
      </form>
    </Modal>
  );
}
